<template lang="pug">
.pp
  .pp-grid
    .pp-title
      product-title.mobile-title(:product="product" :show-h1="true")
    .pp-media(ref="mediaContainer")
      .pp-media-bg
      .pp-media-content(ref="mediaContent")
        slot(name="media")
          rackbuilder(
            v-if="buyRequest && !buyRequest.loading"
            :key="(buyRequest || {}).productId"
            :buyRequest="buyRequest"
            cacheKey="pdp"
            :controls="true"
          )
    .pp-rhpa(ref="rhpaContainer")
      .pp-rhpa-content(ref="rhpaContent")
        product-title.desktop-title(:product="product" :show-h1="true")
        slot(name="rhpa")
          rhpa(v-if="buyRequest" :buy-request="buyRequest" :createRecipe="createProductRecipe")
            template(#rhpaBody)
              RhpaMassStorage(v-if="is3DStorageProduct" :buyRequest='buyRequest')
              RhpaCave(v-else-if="isTheCave" :buyRequest='buyRequest')
              RhpaSlingerX(v-else-if="isSlingerX" :buyRequest='buyRequest')
              RhpaHybridRig(v-else-if="isHybridRig" :buyRequest='buyRequest')
          loading(v-else)
</template>
<script lang="ts" setup>
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, toRef } from 'vue'
import { useRoute } from 'vue-router'
import Rhpa from '../../components/rhpa/rhpa.vue'
import { applyRecipeToBuyRequestTree } from './rackbuilder/utils/mms-utils/buy-request-utils'
import ProductTitle from '@/js/components/rhpa/product-title.vue'
import { useRogue } from '@/js/services/Rogue'
import { setupScrollListener } from '@/js/pages/product/rackbuilder/components/ui/scroll/scrollManager'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import { useMassStorage } from '@/js/composables/useMassStorage'

// Async like product-page-v2: a static import here pulls every custom configurator into pf-product on every PDP.
const RhpaMassStorage = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-mass-storage.vue'))
const RhpaCave = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-cave.vue'))
const RhpaSlingerX = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-slinger-x.vue'))
const RhpaHybridRig = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-hybrid-rig.vue'))

const route = useRoute()
const rogue = useRogue()
const props = defineProps<{
  product?: Product,
  buyRequest?: BuyRequest
}>()

const buyRequestRef = toRef(props, 'buyRequest')
const { is3DStorageProduct } = useMassStorage(buyRequestRef)
const isTheCave = computed(() => isEnabledCustomProduct(rogue, 'cave', props.product))
const isSlingerX = computed(() => isEnabledCustomProduct(rogue, 'slinger-x', props.product))
const isHybridRig = computed(() => isEnabledCustomProduct(rogue, 'hybrid-rig', props.product))

// Refs for DOM elements
const rhpaContent = ref<HTMLElement | null>(null)
const mediaContent = ref<HTMLElement | null>(null)
const mediaContainer = ref<HTMLElement | null>(null)

async function createProductRecipe() {
  if (!is3DStorageProduct.value && !isTheCave.value && !isSlingerX.value && !isHybridRig.value) return false

  let recipeData

  try {
    recipeData = await rogue.fetch('/svc_zeus/product/configs', 'post', {
      buyRequest: props.buyRequest,
    })

    console.log('Recipe created:', recipeData)

    if (props.buyRequest) {
      // Add the recipe to each item in the BR
      applyRecipeToBuyRequestTree(props.buyRequest, recipeData)
    }
  } catch (error) {
    rogue.log.error('Error creating custom product recipe', error as Error)
  }

  return true
}

onMounted(() => {
  const { unmount } = setupScrollListener({ mediaContainer, mediaContent, rhpaContent })

  // Cleanup
  onUnmounted(() => unmount())
})
</script>

<style lang="scss" scoped>
.desktop-title {
  display: none;

}
@include all-desktop(){
  .desktop-title {
    display: block;
  }
  .mobile-title {
    display: none;
  }
}

.pp {
  padding: 0 $space-s;
  max-width: 1920px;
  margin: 0 auto;
  min-width: 0;

  @include desktop(){
    padding: 54px $space-l 0 $space-xxl; 
  }

  @include large-xl(){
    padding: 54px 56px 0 $space-xxxxl; 
  }
}

.pp-media-bg {
  background: #f8f8f8;
  margin-left: -1000px;
  width: calc(100vw + 1000px);
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;

  @include all-desktop(){
    top: -54px;
  }

  @include mobile() {
    margin-left: -1016px;
  }
}

.pp-grid {
  display: grid;
  gap: 0 $space-s;
  grid-template-columns: 1fr;
  grid-template-areas: "title" "hero" "media" "rhpa" "tabs" "micro" "main" "full";
  grid-auto-rows: min-content;

  @include all-desktop(){
    grid-template-areas: "media rhpa" "tabs rhpa" "main rhpa" "fill rhpa" "full full";
    grid-template-rows: min-content min-content min-content min-content min-content 1fr min-content;
  }

  @include desktop(){
    grid-template-columns: minmax(300px, 1fr) 407px; 
    gap: 0 $space-s;
    .pp-media {
      margin-left: -$space-xl;
      width: calc(100vw - 476px);
    }
  }

  @include large-xl(){
    grid-template-columns: minmax(300px, 1fr) 452px; 
    .pp-media {
      margin-left: -64px;
      width: calc(100vw - 562px);
    }
  }

  @include large(){
    gap: 0 $space-l;
  }

  @include xl(){
    gap: 0 56px;
    .pp-media {
      margin-left: calc((-1 * (100vw - 1920px + 176px) / 2) + 24px);
      width: calc(100vw - 570px - ((100vw - 1920px) / 2))
    }
  }
}

.pp-full {
  grid-area: full;
  margin: $space-l 0;
}

.pp-title, .pp-media, .pp-rhpa, .pp-tabs, .pp-main, .pp-full {
  width: 100%;
  min-width: 0;
}

.pp-title {
  grid-area: title;
  background: white;
}

.pp-media {
  grid-area: media;
  position: relative;

  .pp-media-content {
    position: sticky;
  }

  @include all-desktop(){
    min-height: fit-content;
    transition: height 0.3s ease;

    .pp-media-content{
      width: 100%;
      height: 100%;
      max-height: min(calc(100vh - 32px), 1200px);
      transition: top 0.3s ease;
    }
  }

  @include not-desktop(){
    z-index: 1;
    position: sticky;
    transition: top 0.3s ease;

    .pp-media-content {
      margin-left: -$space-s;
      margin-right: -$space-s;
    }
  }
}

@include all-desktop(){
  .pp-title {
    display: none;
  }
}

.pp-rhpa {
  grid-area: rhpa;
  background: white;
}

.pp-rhpa-content {
  padding-top: $space-s;
  background: white;
  z-index: 1;

  @include not-mobile(){
    padding: $space-s;
  }

  @include all-desktop() {
   position: sticky;
  }
}

.pp-tabs {
  grid-area: tabs;
  margin-top: $space-m;
}

.pp-main {
  grid-area: main;
}

.cart-ships-free-badge {
  display: flex;
  margin-top: $space-xs;
}

.cart-ships-free-content {
  padding: $space-xs;
  h5 {
    margin-bottom: $space-xxxs;
  }
  div {
    font-size: $size-s;
  }
}
</style>
